import { create } from 'zustand';
import type { CanvasElement, ConnectorElement } from '@archer/shared';

export type ToolKind =
  | 'select'
  | 'pan'
  | 'rect'
  | 'ellipse'
  | 'diamond'
  | 'parallelogram'
  | 'trapezoid'
  | 'cylinder'
  | 'document'
  | 'person'
  | 'note'
  | 'text'
  | 'arrow'
  | 'line';

export interface CanvasState {
  elements: CanvasElement[];
  selectedIds: string[];
  tool: ToolKind;
  viewport: { x: number; y: number; scale: number };
  showGrid: boolean;
  snapToGrid: boolean;
  setTool: (t: ToolKind) => void;
  setViewport: (v: { x: number; y: number; scale: number }) => void;
  setSelected: (ids: string[]) => void;
  toggleSelected: (id: string, additive?: boolean) => void;
  setElements: (els: CanvasElement[]) => void;
  addElement: (el: CanvasElement) => void;
  addElements: (els: CanvasElement[]) => void;
  updateElement: (id: string, patch: Partial<CanvasElement>) => void;
  removeElements: (ids: string[]) => void;
  clearSelection: () => void;
  setShowGrid: (b: boolean) => void;
  setSnapToGrid: (b: boolean) => void;
}

export const useCanvasStore = create<CanvasState>((set, get) => ({
  elements: [],
  selectedIds: [],
  tool: 'select',
  viewport: { x: 0, y: 0, scale: 1 },
  showGrid: true,
  snapToGrid: true,

  setTool(tool) {
    set({ tool, selectedIds: tool === 'select' ? get().selectedIds : [] });
  },
  setViewport(viewport) {
    set({ viewport });
  },
  setSelected(selectedIds) {
    set({ selectedIds });
  },
  toggleSelected(id, additive = false) {
    set((s) => {
      const has = s.selectedIds.includes(id);
      const selectedIds = additive
        ? has
          ? s.selectedIds.filter((x) => x !== id)
          : [...s.selectedIds, id]
        : [id];
      return { selectedIds };
    });
  },
  setElements(elements) {
    set({ elements });
  },
  addElement(el) {
    set((s) => ({ elements: [...s.elements, el] }));
  },
  addElements(els) {
    set((s) => ({ elements: [...s.elements, ...els] }));
  },
  updateElement(id, patch) {
    set((s) => ({
      elements: s.elements.map((e) => (e.id === id ? ({ ...e, ...patch } as CanvasElement) : e)),
    }));
  },
  removeElements(ids) {
    const set2 = new Set(ids);
    set((s) => ({
      elements: s.elements
        .filter((e) => !set2.has(e.id))
        .map((e) => {
          if (e.type === 'arrow' || e.type === 'line') {
            const c = e as ConnectorElement;
            return {
              ...c,
              source: c.source?.elementId && set2.has(c.source.elementId) ? undefined : c.source,
              target: c.target?.elementId && set2.has(c.target.elementId) ? undefined : c.target,
            } as ConnectorElement;
          }
          return e;
        }),
      selectedIds: [],
    }));
  },
  clearSelection() {
    set({ selectedIds: [] });
  },
  setShowGrid(b) {
    set({ showGrid: b });
  },
  setSnapToGrid(b) {
    set({ snapToGrid: b });
  },
}));
